import { FC } from 'react';
import {
  Button,
  ButtonGroup,
  ModalIcon,
  Spacer,
  Stack,
  TextBody,
  TextTitle,
} from '@pypestream/design-system';
import { TranslationComponent } from '@pypestream/translations';

import { GenericModal, GenericModalProps } from '..';

export type ConfirmDeleteModalProps = Pick<GenericModalProps, 'modalRef'> & {
  /** What is being deleted, shown in the body. */
  target: string;
  onCancel: () => void;
  onConfirm: () => void;
};

/**
 * Confirmation for an irreversible file delete. The old Actions page guarded this with a
 * popover in the table cell; the mockup dropped it, but deleting a script cannot be
 * undone, so the guard is kept — as a modal, which is what the rest of this app uses.
 */
export const ConfirmDeleteModal: FC<ConfirmDeleteModalProps> = ({
  modalRef,
  target,
  onCancel,
  onConfirm,
}) => (
  <GenericModal
    test-id="ps-confirm-delete-modal"
    modalRef={modalRef}
    onClose={onCancel}
  >
    <Stack justifyContent="center">
      <ModalIcon name="error" />
    </Stack>
    <TextTitle size="small" textAlign="center">
      <TranslationComponent i18nKey="pro-studio/actions:confirm_delete_modal.title">
        Delete this file?
      </TranslationComponent>
    </TextTitle>
    <Spacer size="small" />
    <TextBody
      test-id="ps-confirm-delete-modal-subtitle"
      variant="secondary"
      textAlign="center"
    >
      {target}
    </TextBody>
    <ButtonGroup slot="footer">
      <Button
        test-id="ps-confirm-delete-modal-cancel"
        width="full"
        variant="secondary"
        size="large"
        onClick={onCancel}
      >
        <TranslationComponent i18nKey="pro-studio/actions:confirm_delete_modal.buttons.cancel">
          Cancel
        </TranslationComponent>
      </Button>
      <Button
        test-id="ps-confirm-delete-modal-confirm"
        width="full"
        variant="warning"
        size="large"
        onClick={onConfirm}
      >
        <TranslationComponent i18nKey="pro-studio/actions:confirm_delete_modal.buttons.confirm">
          Delete
        </TranslationComponent>
      </Button>
    </ButtonGroup>
  </GenericModal>
);
